import React, { useEffect } from 'react';
import { toArray, filter, orderBy, pick } from 'lodash-es';
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
import { VehicleModel, buildStylesPath } from 'client/data/models/vehicle-v2';
import { Select } from 'site-modules/shared/components/select/select';
import { StyledSelect, StyledSelectPropTypes } from 'site-modules/shared/components/styled-select/styled-select';
import { connect } from 'react-redux';
import { getAppraiserStylesPath, selectEmo4182Context } from 'client/data/utils/appraiser-preload-experiment';
import { DEFAULT_CLASSNAMES } from './constants';

export const sortById = (data, styleIds) => {
  const ids = styleIds.split(',').map(id => parseInt(id, 10));
  return filter(data, style => ids.includes(style.id));
};

export const withSortedByStyleIds = WrappedComponent => props => {
  const { onChange, options, styleIds, updateValueOnChanges } = props;

  useEffect(() => {
    if (styleIds && updateValueOnChanges) {
      const sortedOptions = sortById(options, styleIds);
      onChange(sortedOptions[0]);
    }
  }, [styleIds, updateValueOnChanges, options, onChange]);

  return <WrappedComponent {...pick(props, Object.keys(StyledSelectPropTypes))} />;
};

export const mapStateToProps = (state, props) => {
  const { options = [], value, styleIds } = props;
  if (!styleIds) {
    return {};
  }
  const sortedOptions = sortById(options, styleIds);
  let selectedValue = '';
  let updateValueOnChanges = false;

  if (sortedOptions.length === 1 && !value) {
    selectedValue = sortedOptions[0].slug;
    updateValueOnChanges = true;
  } else if (options.some(opt => opt.slug === value)) {
    selectedValue = value;
  }

  return {
    value: selectedValue,
    options: sortedOptions.length > 1 ? sortedOptions : options,
    updateValueOnChanges,
  };
};

export const mapStateToPropsWithPageContext = (state, props) => ({
  ...mapStateToProps(state, props),
  ...selectEmo4182Context(state),
});

export const stateToPropsConfig = {
  options: bindToPath(
    props => {
      const { make, model, submodel, year } = props;
      let path = null;

      if (make && model && submodel && year) {
        path = buildStylesPath({ make: make.slug, model: model.slug, submodel: submodel.slug, year: year.year });
      } else if (make && model && year) {
        path = getAppraiserStylesPath({
          pageName: props.pageName,
          location: props.location,
          emo4182Recipe: props.emo4182Recipe,
          make: make.slug,
          model: model.slug,
          year: year.year,
        });
      }

      return path;
    },
    VehicleModel,
    trimData => orderBy(toArray(trimData), 'name')
  ),
};

const ConnectedStyleDropdown = connect(mapStateToPropsWithPageContext)(connectToModel(Select, stateToPropsConfig));

const ConnectedStyledStyleDropdown = connect(mapStateToPropsWithPageContext)(
  connectToModel(StyledSelect, stateToPropsConfig)
);

const ConnectedStyleDropdownWithSortedStyleId = connectToModel(
  connect(mapStateToPropsWithPageContext)(connectToModel(withSortedByStyleIds(Select), {})),
  stateToPropsConfig
);

const ConnectedStyledStyleDropdownWithSortedStyleId = connectToModel(
  connect(mapStateToPropsWithPageContext)(connectToModel(withSortedByStyleIds(StyledSelect), {})),
  stateToPropsConfig
);

export function StyleDropdown({
  name = 'select-style',
  toggle = 'Select Style',
  valueKey = 'slug',
  labelKey = 'name',
  className = `js-style-select ${DEFAULT_CLASSNAMES}`,
  ...restProps
}) {
  return (
    <ConnectedStyleDropdown
      {...restProps}
      name={name}
      toggle={toggle}
      valueKey={valueKey}
      labelKey={labelKey}
      className={className}
    />
  );
}

export function StyledStyleDropdown({
  name = 'select-style',
  toggle = 'Select Style',
  valueKey = 'slug',
  labelKey = 'name',
  ...restProps
}) {
  return (
    <ConnectedStyledStyleDropdown {...restProps} name={name} toggle={toggle} valueKey={valueKey} labelKey={labelKey} />
  );
}

export function StyleDropdownWithSortedStyleId({
  name = 'select-style',
  toggle = 'Select Style',
  valueKey = 'slug',
  labelKey = 'name',
  className = `js-style-select ${DEFAULT_CLASSNAMES}`,
  ...restProps
}) {
  return (
    <ConnectedStyleDropdownWithSortedStyleId
      {...restProps}
      name={name}
      toggle={toggle}
      valueKey={valueKey}
      labelKey={labelKey}
      className={className}
    />
  );
}

export function StyledStyleDropdownWithSortedStyleId({
  name = 'select-style',
  toggle = 'Select Style',
  valueKey = 'slug',
  labelKey = 'name',
  className = `js-style-select ${DEFAULT_CLASSNAMES}`,
  ...restProps
}) {
  return (
    <ConnectedStyledStyleDropdownWithSortedStyleId
      {...restProps}
      name={name}
      toggle={toggle}
      valueKey={valueKey}
      labelKey={labelKey}
      className={className}
    />
  );
}
